import { useEffect } from "react";
import { Link } from "wouter";
import { tenderSlug } from "@/lib/tenderSlug";
import { useGetFavourites, type FavouriteTender } from "@/lib/useFavourites";
import { useGetMyProfile } from "@workspace/api-client-react";
import { SeoHead } from "@/components/SeoHead";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, MapPin, Calendar, ChevronRight, Crown } from "lucide-react";
import { useUser } from "@clerk/react";

function FavouriteCard({ fav }: { fav: FavouriteTender }) {
  const slug = tenderSlug(fav.tenderId, fav.title ?? "");
  const isOpen = fav.status === "open";
  const savedDate = fav.savedAt ? new Date(fav.savedAt).toLocaleDateString("zh-HK") : "";
  return (
    <Link href={`/tenders/${slug}`}>
      <Card className="hover:shadow-md transition-all duration-200 cursor-pointer group hover:border-primary/30 hover:bg-primary/[0.03]">
        <CardContent className="p-4 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2 mb-2">
              {fav.title}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {isOpen ? (
                <Badge className="text-[10px] px-1.5 py-0 text-[#2a6f4f] border-[#2a6f4f] bg-[#95ff0040]">招標中</Badge>
              ) : (
                <Badge className="text-[10px] px-1.5 py-0 text-red-600 border-red-300 bg-red-50">已截止</Badge>
              )}
              {fav.district && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" />{fav.district}
                </span>
              )}
              {fav.deadlineDate && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />截止 {fav.deadlineDate}
                </span>
              )}
              {savedDate && (
                <span className="text-xs text-muted-foreground/60 flex items-center gap-1 ml-auto">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />收藏於 {savedDate}
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0 mt-0.5" />
        </CardContent>
      </Card>
    </Link>
  );
}

export default function FavouritesPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const { isSignedIn, isLoaded } = useUser();
  const { data: profile, isLoading: profileLoading } = useGetMyProfile();
  const isPaid = profile?.membershipTier === "paid";
  const { data, isLoading } = useGetFavourites(!!isSignedIn && isPaid);

  if (!isLoaded || profileLoading) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-10 max-w-2xl space-y-3">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-16 max-w-2xl text-center">
        <Star className="h-12 w-12 text-amber-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold mb-2">我的最愛</h1>
        <p className="text-muted-foreground mb-6">請先登入以查看已收藏的招標項目。</p>
        <Link href="/sign-in">
          <Button>登入</Button>
        </Link>
      </div>
    );
  }

  if (!isPaid) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-16 max-w-2xl text-center">
        <Star className="h-12 w-12 text-amber-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold mb-2">我的最愛</h1>
        <p className="text-muted-foreground mb-6">「我的最愛」功能專屬付費會員，升級後即可收藏心水招標，隨時翻查。</p>
        <Link href="/pricing">
          <Button>了解付費計劃</Button>
        </Link>
      </div>
    );
  }

  const favourites = data?.favourites ?? [];

  return (
    <div className="min-h-screen bg-background">
      <SeoHead title="我的最愛｜Skoltend" description="查看已收藏的招標項目" canonical="/favourites" />
      <div className="border-b bg-secondary/20 py-8">
        <div className="container mx-auto px-4 md:px-8 max-w-2xl">
          <div className="flex items-center gap-3">
            <Star className="h-6 w-6 fill-amber-400 text-amber-400" />
            <h1 className="text-2xl font-bold">我的最愛</h1>
            {favourites.length > 0 && (
              <Badge variant="secondary" className="ml-1">{favourites.length} 項</Badge>
            )}
          </div>
          <p className="text-muted-foreground text-sm mt-1">已收藏的招標項目，在招標詳情頁按 ★ 即可加入</p>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8 max-w-2xl">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}
          </div>
        ) : favourites.length === 0 ? (
          <div className="text-center py-16">
            <Star className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground font-medium mb-1">還沒有收藏任何招標</p>
            <p className="text-muted-foreground/60 text-sm mb-6">在招標詳情頁按右上角的 ★ 即可收藏</p>
            <Link href="/tenders">
              <Button variant="outline">瀏覽所有招標</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {favourites.map(fav => (
              <FavouriteCard key={fav.tenderId} fav={fav} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
